import { ChangeEvent, FC, useEffect } from "react";
import { boardMember, ceo, chairperson, ProcuraForm } from "./procuraDomain";
import {
  CollapseList,
  LocalizedMarkdownQuestion,
  YesNoQuestion,
} from "@/dbot/common/commonComponents";
import { observer } from "mobx-react";
import { LinkButton } from "@/components-v2/Button";
import { Box, VBox } from "@/components-v2/Box";
import { RadioButtonGroup } from "@/components-v2/RadioButton";
import { identity } from "lodash";
import { Checkbox } from "@/components-v2/Checkbox";
import { Dialog } from "@/components-v2/Dialog";
import { MTextInput } from "@/components-v2/TextInput";
import { TMessageKey } from "@/i18n/types.gen";

import { useTranslation } from "@/stores/RootStoreContext";

export const ProcuraView: FC<{ form: ProcuraForm }> = observer(({ form }) => {
  const { t } = useTranslation();

  useEffect(() => form.init(), [form]);

  const toggleProcura = (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      let combinationNotAllowed: string[] = [];
      if (ceo.some((d) => d.id === e.target.value))
        combinationNotAllowed = (ceo?.find((d) => d.id === e.target.value)?.notCombinedWith ||
          []) as string[];
      else if (chairperson.some((d) => d.id === e.target.value))
        combinationNotAllowed = (chairperson?.find((d) => d.id === e.target.value)
          ?.notCombinedWith || []) as string[];
      else if (boardMember.some((d) => d.id === e.target.value))
        combinationNotAllowed = (boardMember?.find((d) => d.id === e.target.value)
          ?.notCombinedWith || []) as string[];

      form.procuraAssignedToByRole.select(e.target.value);
      combinationNotAllowed.forEach((id) => form.procuraAssignedToByRole.unselect(id));
    } else {
      form.procuraAssignedToByRole.unselect(e.target.value);
    }
  };

  return (
    <VBox gap={"md"}>
      <VBox>
        <div>{t("decisions.procura.whoReceivedProcura.questionText")}</div>
        <Checkbox
          label={t("decisions.procura.whoReceivedProcura.withRole")}
          checked={form.isProcuraAssignedByRole.value}
          onChange={() => form.toggleIsProcuraAssignedByRole()}
          disabled={form.isProjectLocked}
        />
        <Checkbox
          label={t("decisions.procura.whoReceivedProcura.byName")}
          checked={form.isProcuraAssignedByName.value}
          onChange={() => form.toggleIsProcuraAssignedByName()}
          disabled={form.isProjectLocked}
        />
        <Checkbox
          label={t("decisions.procura.whoReceivedProcura.byFreetext")}
          checked={form.isProcuraAssignedByFreetext.value}
          onChange={() => form.toggleIsProcuraAssignedByFreetext()}
          disabled={form.isProjectLocked}
        />
      </VBox>
      {form.isProcuraAssignedByRole.value && (
        <>
          <VBox>
            <div>{t("decisions.procura.ceo.title")}</div>
            {ceo.map((ceo) => {
              const label = (): TMessageKey => {
                switch (ceo.id) {
                  case "ceoAlone":
                    return "decisions.procura.ceo.ceoAlone";
                  case "ceoOrChairpersonSeparate":
                    return "decisions.procura.ceo.ceoOrChairpersonSeparate";
                  case "ceoOrMembersSeparate":
                    return "decisions.procura.ceo.ceoOrMembersSeparate";
                  case "ceoAndChairpersonTogether":
                    return "decisions.procura.ceo.ceoAndChairpersonTogether";
                  default:
                    return "decisions.procura.ceo.ceoAndMembersTogether";
                }
              };
              return (
                <Checkbox
                  key={ceo.id}
                  name="procuraAssignedTo"
                  label={t(label())}
                  checked={form.procuraAssignedToByRole.isSelected(ceo.id)}
                  value={ceo.id}
                  onChange={(event) => toggleProcura(event, event.target.checked)}
                  disabled={form.isProjectLocked}
                />
              );
            })}
          </VBox>
          <VBox>
            <div>{t("decisions.procura.chairperson.title")}</div>
            {chairperson.map((chairperson) => {
              const label = (): TMessageKey => {
                switch (chairperson.id) {
                  case "chairpersonAlone":
                    return "decisions.procura.chairperson.chairpersonAlone";
                  case "chairpersonAloneOrTwoMembersTogether":
                    return "decisions.procura.chairperson.chairpersonAloneOrTwoMembersTogether";
                  case "chairpersonAndMemberTogether":
                    return "decisions.procura.chairperson.chairpersonAndMemberTogether";
                  default:
                    return "decisions.procura.chairperson.chairpersonAndTwoMembersTogether";
                }
              };
              return (
                <Checkbox
                  key={chairperson.id}
                  name="procuraAssignedTo"
                  label={t(label())}
                  checked={
                    form.procuraAssignedToByRole &&
                    form.procuraAssignedToByRole.isSelected(chairperson.id)
                  }
                  value={chairperson.id}
                  onChange={(event) => toggleProcura(event, event.target.checked)}
                  disabled={form.isProjectLocked}
                />
              );
            })}
          </VBox>
          <VBox>
            <div>{t("decisions.procura.boardMember.title")}</div>
            {boardMember.map((boardMember) => {
              const label = (): TMessageKey => {
                switch (boardMember.id) {
                  case "memberSeparate":
                    return "decisions.procura.boardMember.memberSeparate";
                  case "twoMembersTogether":
                    return "decisions.procura.boardMember.twoMembersTogether";
                  case "threeMembersTogether":
                    return "decisions.procura.boardMember.threeMembersTogether";
                  default:
                    return "decisions.procura.boardMember.membersTogether";
                }
              };
              return (
                <Checkbox
                  key={boardMember.id}
                  name="procuraAssignedTo"
                  label={t(label())}
                  checked={
                    form.procuraAssignedToByRole &&
                    form.procuraAssignedToByRole.isSelected(boardMember.id)
                  }
                  value={boardMember.id}
                  onChange={(event) => toggleProcura(event, event.target.checked)}
                  disabled={form.isProjectLocked}
                />
              );
            })}
          </VBox>
        </>
      )}
      {form.isProcuraAssignedByName.value === true && (
        <VBox>
          <div>{t("decisions.procura.byName.title")}</div>
          {form.procuraAssignedToByName?.value.map((procuraByName) => {
            return (
              <Checkbox
                key={String(procuraByName.id)}
                label={form.procuraAssignedToByName.formatToString(procuraByName, t)}
                checked={form.procuraAssignedToByName.isSelected(procuraByName.id)}
                value={String(procuraByName.id)}
                onChange={(event) =>
                  form.toggleProcuraAssignedToByName(procuraByName.id, event.target.checked)
                }
                disabled={form.isProjectLocked}
              />
            );
          })}
          <Box key="buttonAddProcuraByNameMemberDialog">
            <LinkButton
              label={t("decisions.procura.byName.add-member")}
              icon={"plus"}
              onClick={() => form.displayProcuraByNameDialog()}
              disabled={form.isProjectLocked}
            />
          </Box>
          {form.showProcuraByNameDialog && (
            <Dialog
              title={t("decisions.procura.byName.add-procura")}
              submitLabel={t("decisions.save")}
              onSubmit={() => form.addNewProcuraAssignedToByName()}
              onCancel={() => form.hideProcuraByNameDialog()}
              isSubmitDisabled={!form.procuraByName.isValid}
            >
              <VBox gap={"md"}>
                <VBox>
                  <div>{t("decisions.procura.byName.typeOfProcura.questionText")}</div>
                  <RadioButtonGroup
                    name={"isAlone"}
                    options={[true, false]}
                    horizontal
                    idFn={identity}
                    labelFn={(value) =>
                      value
                        ? t("decisions.procura.byName.typeOfProcura.procuraAlone")
                        : t("decisions.procura.byName.typeOfProcura.procuraTogether")
                    }
                    selected={form.procuraByName.isAlone.value ?? undefined}
                    onChange={(_, option) => form.procuraByName.setIsAlone(option)}
                  />
                </VBox>
                {form.procuraByName.isAlone.value && (
                  <MTextInput
                    autofocus
                    label={t("decisions.name")}
                    model={form.procuraByName.assignedAloneByName}
                  />
                )}
                {form.procuraByName.isAlone.value === false && (
                  <VBox>
                    <div id="procuraAssignedToNamedBoardMembers">
                      {t("decisions.procura.byName.namedPerson.add-procura-together")}
                    </div>
                    <div id="procuraAssignedToNamedBoardMembers-label">
                      {t("decisions.procura.byName.namedPerson.choose-board-members")}
                    </div>
                    <CollapseList
                      closeLabel={t("collapse")}
                      openLabel={t(
                        "decisions.procura.byName.namedPerson.choose-board-members.showAll",
                      )}
                      initiallyExpanded={
                        form.procuraByName.assignedToBoardMembers.selectedCount > 0
                      }
                    >
                      {form.procuraByName.assignedToBoardMembers.value.map((companyBoardMember) => {
                        return companyBoardMember ? (
                          <Checkbox
                            key={companyBoardMember.id}
                            label={companyBoardMember.name}
                            onChange={() =>
                              form.procuraByName.toggleAssignedToBoardMembers(companyBoardMember.id)
                            }
                            checked={companyBoardMember.isSelected}
                            disabled={form.isProjectLocked}
                          />
                        ) : null;
                      })}
                    </CollapseList>
                    <VBox>
                      <div id="procuraAssignedToNamedPerson-label">
                        {t("decisions.procura.byName.namedPerson.choose-named-persons")}
                      </div>
                      {form.procuraByName.assignedTogetherByName.map((procuraByName, index) => {
                        return (
                          <Box key={"together_" + index}>
                            <MTextInput
                              autofocus
                              label={t("decisions.name")}
                              model={procuraByName}
                            />
                          </Box>
                        );
                      })}
                      <Box>
                        <LinkButton
                          label={t("decisions.procura.byName.add-member")}
                          icon={"plus"}
                          onClick={() => form.procuraByName.addNewByName()}
                          key={"addnew"}
                        />
                      </Box>
                    </VBox>
                  </VBox>
                )}
              </VBox>
            </Dialog>
          )}
        </VBox>
      )}
      {form.isProcuraAssignedByFreetext.value === true && (
        <LocalizedMarkdownQuestion
          name={"procura-freetexts"}
          question={t("decisions.procura.freetext.questionText")}
          textModels={form.freetexts}
          isLocked={form.isProjectLocked}
        />
      )}
      <YesNoQuestion
        question={t("decisions.procura.revoke.questionText")}
        field={form.revokeBoardDecisionsProcura}
        isLocked={form.isProjectLocked}
      />
    </VBox>
  );
});
